// client/src/pages/seo/OnlineTableReservationForRestaurantsEn.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Does your restaurant need online table reservation?",
  },
  {
    id: "definition",
    label: "What is online table reservation?",
  },
  {
    id: "phone",
    label: "Why phone reservations are not always enough",
  },
  {
    id: "always-on",
    label: "Online reservation works outside opening hours",
  },
  {
    id: "menu",
    label: "How online reservation connects with a digital menu",
  },
  {
    id: "planning",
    label: "How online reservation helps with planning",
  },
  {
    id: "no-show",
    label: "No-shows: what online reservation can and cannot solve",
  },
  {
    id: "for-whom",
    label: "Which restaurants benefit most from online reservation?",
  },
  {
    id: "mistakes",
    label: "Common online reservation mistakes",
  },
  {
    id: "bestplace",
    label: "How Best Place can help with online reservation",
  },
  {
    id: "faq",
    label: "FAQ about online table reservation",
  },
];

const checklist = [
  "Guests often call during peak service hours.",
  "Your team cannot always answer the phone immediately.",
  "Reservations come through Google, Instagram, your website, or messages.",
  "You want to accept reservation requests outside opening hours.",
  "You want to plan tables and guests more clearly in advance.",
  "You want less chaos with notes, calls, or messages.",
  "You want date, time, party size, and contact details in one place.",
  "Reservation should connect with your menu or restaurant page.",
  "Your restaurant should feel easier for younger guests and tourists.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Online reservation may not be urgent yet",
      text:
        "Phone or a simple contact option may still be enough. But a small online reservation flow can help you start collecting requests in a more structured way.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "A test makes sense",
      text:
        "Online table reservation can help your restaurant reduce phone stress, collect reservation details more clearly, and give guests a more convenient path.",
      tone: "medium",
    };
  }

  return {
    title: "Online reservation can become an important part of your system",
    text:
      "Several points suggest that digital reservations could help your restaurant: better planning, fewer missed requests, a shorter path from menu to booking, and easier access for guests.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function OnlineTableReservationForRestaurantsEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Online Table Reservation for Restaurants | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/online-table-reservation-for-restaurants",
          de: "/de/online-tischreservierung",
          no: "/no/online-bordreservasjon-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Online table reservation for restaurants: less phone stress, more
              predictable guests
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Online reservation does not replace hospitality. It removes
                unnecessary friction before the guest even arrives.
              </p>

              <p className="seoHero__text">
                When a guest has seen your menu, found your restaurant on
                Google, or opened your page through a QR code, the next question
                is simple: can I reserve a table right now?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Does your restaurant need online table reservation?</h2>

            <p>
              Press the statements that sound true for your restaurant. The
              result is intentionally honest: not every restaurant needs online
              reservation immediately, but many restaurants can benefit from a
              simple booking flow earlier than they think.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>What is online table reservation?</h2>

          <p>
            Online table reservation means that guests can choose a date, time,
            party size, and contact details through a restaurant website or
            online restaurant page.
          </p>

          <p>
            For guests, this is convenient because they do not need to call,
            wait, or send messages through social media. For restaurants, it
            creates a clearer process: reservations are not scattered across
            phone calls, notes, Instagram messages, and memory.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Date.</li>
            <li className="text__seo-li">Time.</li>
            <li className="text__seo-li">Party size.</li>
            <li className="text__seo-li">Name.</li>
            <li className="text__seo-li">Phone or email.</li>
            <li className="text__seo-li">Comment.</li>
            <li className="text__seo-li">Reservation status.</li>
          </ul>

          <h3>Reservations should be structured, not scattered</h3>

          <p>
            The main advantage is not only that the guest can send a request
            online. The restaurant also receives the important information in a
            clearer format.
          </p>
        </section>

        <section id="phone" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why phone reservations are not always enough</h2>

            <p>
              The phone remains important. But it has one weakness: it only
              works well when someone has time to answer immediately.
            </p>

            <h3>A missed call can become a missed table</h3>

            <p>
              During peak hours, the team may be in service, guests may be
              waiting, the kitchen may be busy, and the phone may ring at the
              worst possible moment.
            </p>

            <p>
              Online reservation does not remove the phone. It adds a second
              channel that can work in parallel.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Phone only</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Someone must answer immediately.
                  </li>
                  <li className="text__seo-li">
                    Calls interrupt service.
                  </li>
                  <li className="text__seo-li">
                    Details can be written down incorrectly.
                  </li>
                  <li className="text__seo-li">
                    Requests outside opening hours may be missed.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Phone + online request</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Guests can still call if they prefer.
                  </li>
                  <li className="text__seo-li">
                    Online requests collect key details.
                  </li>
                  <li className="text__seo-li">
                    The team can review requests later.
                  </li>
                  <li className="text__seo-li">
                    Fewer reservations are lost during busy moments.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="always-on" className="acquaintance">
          <h2>Online reservation works outside opening hours</h2>

          <p>
            Guests do not always plan restaurant visits during your opening
            hours. They may decide late at night, on a weekend, while traveling,
            or directly after reading your menu.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>23:14:</strong> Guest views your menu on a smartphone.
            </li>

            <li className="text__seo-li">
              <strong>23:16:</strong> Guest selects date, time, and party size.
            </li>

            <li className="text__seo-li">
              <strong>23:17:</strong> Reservation request is sent.
            </li>

            <li className="text__seo-li">
              <strong>Morning:</strong> Your restaurant sees the new request.
            </li>
          </ol>

          <p>
            This is the practical value: online reservation keeps working when
            your team is not available on the phone.
          </p>

          <VisualIdea>
            A simple evening flow: guest opens the menu at night, clicks reserve,
            sends a request and the restaurant sees it in the morning.
          </VisualIdea>
        </section>

        <section id="menu" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>How online reservation connects with a digital menu</h2>

            <p>
              Digital menu and online reservation should not be treated as
              separate ideas. Often, the guest first checks the menu, looks at
              prices, images, and offers — and then decides whether to visit.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">View menu.</li>
              <li className="text__seo-li">Check dishes and prices.</li>
              <li className="text__seo-li">Interest appears.</li>
              <li className="text__seo-li">Reserve table.</li>
            </ol>

            <p>
              If a clear “Reserve a table” button appears after the menu, the
              path becomes shorter. The menu answers: what can I get here? The
              reservation answers: how can I visit?
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/digital-menu-for-restaurants">
                    Digital menu for restaurants
                  </Link>
                </p>
              </div>

              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/qr-code-menu-for-restaurants">
                    QR code menu for restaurants
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="planning" className="acquaintance">
          <h2>How online reservation helps with planning</h2>

          <p>
            When reservations arrive through many different channels, chaos is
            easy. Online reservation helps collect important information in a
            consistent format.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">When will the guest arrive?</li>
            <li className="text__seo-li">How many people?</li>
            <li className="text__seo-li">Is there a comment?</li>
            <li className="text__seo-li">Is the reservation confirmed?</li>
            <li className="text__seo-li">Are there special notes?</li>
            <li className="text__seo-li">Does someone need a callback?</li>
          </ul>

          <p>
            The better a restaurant sees its reservations, the better it can
            plan the evening: tables, staff, kitchen workload, peak hours, and
            larger groups.
          </p>
        </section>

        <section id="no-show" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>No-shows: what online reservation can and cannot solve</h2>

            <p>
              Online reservation is not a guarantee against no-shows. Guests can
              still fail to arrive. But it can make the process clearer and more
              structured.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Contact details are collected clearly.
              </li>
              <li className="text__seo-li">
                Confirmations are easier to understand.
              </li>
              <li className="text__seo-li">
                Cancellations can become simpler.
              </li>
              <li className="text__seo-li">
                Comments and special requests remain documented.
              </li>
              <li className="text__seo-li">
                The team sees reservations in one place.
              </li>
            </ul>

            <p>
              The honest point is this: digital reservation does not solve every
              problem. But it can help restaurants capture reservation details
              better and make daily service decisions easier.
            </p>
          </div>
        </section>

        <section id="for-whom" className="acquaintance">
          <h2>Which restaurants benefit most from online reservation?</h2>

          <p>
            Online reservation is not equally important for every concept. It is
            especially useful where table planning, peak times, or group
            bookings matter.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Especially useful for</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Restaurants with evening peaks.</li>
                <li className="text__seo-li">Family restaurants.</li>
                <li className="text__seo-li">Pizzerias with seating.</li>
                <li className="text__seo-li">Brunch cafés.</li>
                <li className="text__seo-li">Tourist restaurants.</li>
                <li className="text__seo-li">Hotel restaurants.</li>
                <li className="text__seo-li">Restaurants with terraces.</li>
                <li className="text__seo-li">Group reservations.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Less critical for</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Pure takeaway concepts.</li>
                <li className="text__seo-li">Food trucks.</li>
                <li className="text__seo-li">
                  Very small cafés with only a few tables.
                </li>
                <li className="text__seo-li">Businesses without seating.</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Common online reservation mistakes</h2>

            <p>
              Online reservation helps only when the flow is simple, clear and
              connected with real restaurant operations.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    The form asks for too much information.
                  </li>
                  <li className="text__seo-li">
                    Guests do not know whether the reservation is confirmed.
                  </li>
                  <li className="text__seo-li">
                    The mobile experience is weak.
                  </li>
                  <li className="text__seo-li">
                    The reservation button is hidden.
                  </li>
                  <li className="text__seo-li">
                    The team sees reservations too late.
                  </li>
                  <li className="text__seo-li">
                    There is no phone alternative for guests who prefer calling.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Keep the form short and clear.
                  </li>
                  <li className="text__seo-li">
                    Explain whether it is a request or confirmed booking.
                  </li>
                  <li className="text__seo-li">
                    Make the form work well on smartphones.
                  </li>
                  <li className="text__seo-li">
                    Place the reservation button after the menu.
                  </li>
                  <li className="text__seo-li">
                    Notify the restaurant clearly.
                  </li>
                  <li className="text__seo-li">
                    Keep phone and online reservation working together.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Frequently asked questions about online table reservation</h2>

          <div className="seoFaq">
            <details>
              <summary>Does every restaurant need online reservation?</summary>
              <p>
                No. For pure takeaway concepts or very small businesses, it may
                be less important. For restaurants with seating, peak hours, or
                group reservations, testing it makes more sense.
              </p>
            </details>

            <details>
              <summary>Does online reservation replace the phone?</summary>
              <p>
                Not necessarily. It often complements the phone and gives guests
                a second, more convenient channel.
              </p>
            </details>

            <details>
              <summary>Can guests reserve outside opening hours?</summary>
              <p>
                Yes. That is one of the main benefits: guests can send a request
                even when nobody is available on the phone.
              </p>
            </details>

            <details>
              <summary>Can online reservation prevent no-shows?</summary>
              <p>
                It cannot prevent them completely. But it can help collect
                contact details and reservation information more clearly.
              </p>
            </details>

            <details>
              <summary>Can reservation be connected with a digital menu?</summary>
              <p>
                Yes. This is a very natural path: the guest checks the menu and
                can then reserve a table immediately.
              </p>
            </details>

            <details>
              <summary>Does the guest need an app?</summary>
              <p>
                No. The best experience is usually a browser-based reservation
                flow on a smartphone.
              </p>
            </details>
          </div>
        </section>
        <section className="articls qrArticle__related">
                          <h2>Weitere Themen rund um digitale Speisekarten und Reservierungen</h2>
                
                          <div className="seoCompareSmall">
                            <p>
                              <Link to="/en/digital-drinks-menu-bar">
                                Digital drink menu for bars
                              </Link>
                            </p>
                                    
                            <p>
                              <Link to="/en/restaurant-website-for-restaurants">
                                Restaurant website, reservations and trust in one place.
                              </Link>
                            </p>
                            
                
                            <p>
                              <Link to="/en/digital-ordering-system-for-restaurants">
                                Digital ordering system for restaurants
                              </Link>
                            </p>
                
                            <p>
                              <Link to="/en/restaurant-digitalization">
                                The gradual transition of restaurants to digital technologies
                              </Link>
                            </p>

                            <p>
                              <Link to="/en/digital-menu-comparison">
                                Comparison of digital menu providers
                              </Link>
                            </p>
                          </div>
                        </section>
      </article>
    </main>
  );
}